import { formatInr } from "@/lib/format/inr";
import { formatRelativeTime } from "@/lib/format/relative-time";
import { CategoryIcon } from "@/components/category-icon/category-icon";
import type { UserDetail } from "@/lib/users/user-detail";
import type { UserGroupItem } from "@/lib/users/user-group-item";
import type { UserExpenseItem } from "@/lib/users/user-expense-item";
import type { UserSessionItem } from "@/lib/users/user-session-item";
import { formatAdminDate, formatAdminDateTime } from "@/lib/format/admin-time";

export interface UserProfileViewProps {
  user: UserDetail;
  now: Date;
  groups?: UserGroupItem[];
  recentExpenses?: UserExpenseItem[];
  sessions?: UserSessionItem[];
}

function initials(name: string): string {
  return (
    name
      .split(/\s+/)
      .slice(0, 2)
      .map(p => p[0]?.toUpperCase() ?? "")
      .join("") || "??"
  );
}

// lastActiveAt is a moment: its day on the admins' clock (IST).
function toDate(d: Date | string | null): string {
  return d ? formatAdminDate(d) : "—";
}

// Moments: admins' clock (IST), labelled.
function toDateTime(d: Date | string | null): string {
  return d ? formatAdminDateTime(d) : "—";
}

function Chip({
  tone = "neutral",
  children,
}: {
  tone?: "neutral" | "good" | "warn" | "info";
  children: React.ReactNode;
}) {
  const cls = {
    neutral: "text-fc-text-secondary border-fc-border bg-fc-overlay",
    good: "text-fc-brand border-fc-brand/40 bg-fc-brand/10",
    warn: "text-fc-warn border-fc-warn/40 bg-fc-warn/10",
    info: "text-fc-info border-fc-info/40 bg-fc-info/10",
  }[tone];
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] ${cls}`}
    >
      {children}
    </span>
  );
}

function Metric({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="flex flex-col gap-1.5 rounded-xl border border-fc-border bg-gradient-to-b from-fc-raised to-fc-overlay p-4">
      <div className="text-[10.5px] uppercase tracking-wide text-fc-text-dim">
        {label}
      </div>
      <div className="font-mono text-[22px] font-semibold tracking-tight text-fc-text">
        {value}
      </div>
      {sub ? (
        <div className="text-[10.5px] text-fc-text-dim">{sub}</div>
      ) : null}
    </div>
  );
}

function PropRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <>
      <div className="pt-0.5 text-[10.5px] uppercase tracking-wide text-fc-text-dim">
        {label}
      </div>
      <div className="text-[12.5px] text-fc-text">{children}</div>
    </>
  );
}

export function UserProfileView({
  user,
  now,
  groups,
  recentExpenses,
  sessions,
}: UserProfileViewProps) {
  const totalGroups = user.personalGroupCount + user.nonPersonalGroupCount;
  const showGroupsCard = groups && groups.length > 0;
  const showExpensesCard = recentExpenses && recentExpenses.length > 0;
  const showSessionsCard = sessions && sessions.length > 0;

  return (
    <div className="flex flex-col gap-6">
      {user.deletedAt ? (
        <section className="rounded-xl border border-fc-bad/40 bg-fc-bad/10 p-4">
          <div className="text-[11px] uppercase tracking-wide text-fc-bad">
            Deleted
          </div>
          <div className="mt-1 text-[12.5px] text-fc-text">
            Deleted by{" "}
            <span className="font-medium">{user.deletedBy ?? "—"}</span>
            {user.deletedAt
              ? ` · ${formatRelativeTime(user.deletedAt, now)}`
              : ""}
            {user.anonymized ? " · anonymized" : ""}
          </div>
        </section>
      ) : user.suspendedAt ? (
        <section className="rounded-xl border border-fc-warn/40 bg-fc-warn/10 p-4">
          <div className="text-[11px] uppercase tracking-wide text-fc-warn">
            Suspended
          </div>
          <div className="mt-1 text-[12.5px] text-fc-text">
            By <span className="font-medium">{user.suspendedBy ?? "—"}</span>
            {user.suspendedAt
              ? ` · ${formatRelativeTime(user.suspendedAt, now)}`
              : ""}
          </div>
          {user.suspendReason ? (
            <div className="mt-1 text-[12px] text-fc-text-secondary">
              Reason: {user.suspendReason}
            </div>
          ) : null}
        </section>
      ) : null}

      {/* HERO */}
      <section className="flex items-center gap-4">
        <div className="grid h-[72px] w-[72px] place-items-center rounded-2xl bg-gradient-to-br from-violet-400 to-violet-700 text-[26px] font-semibold text-white">
          {initials(user.name)}
        </div>
        <div className="min-w-0">
          <h1 className="text-[24px] font-semibold tracking-tight text-fc-text">
            {user.name}
          </h1>
          <div className="font-mono text-[13px] text-fc-text-dim">
            {user.email}
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {user.emailVerified ? (
              <Chip tone="good">verified</Chip>
            ) : (
              <Chip tone="warn">pending</Chip>
            )}
            <Chip>{user.authProvider}</Chip>
            <Chip tone="info">{user.locale}</Chip>
            <span className="font-mono text-[10.5px] tracking-wider text-fc-text-dim">
              u_{user.id.slice(0, 8)}
            </span>
          </div>
        </div>
      </section>

      {/* METRIC STRIP — 5 tiles */}
      <section className="grid grid-cols-5 gap-3">
        <Metric
          label="Groups"
          value={String(totalGroups)}
          sub={`${user.personalGroupCount} personal · ${user.nonPersonalGroupCount} family`}
        />
        <Metric
          label="Expenses"
          value={String(user.expenseCount)}
          sub={`30d · ${user.expenseCount30d}`}
        />
        <Metric label="Total spent" value={formatInr(user.totalSpent)} />
        <Metric label="Feedback" value={String(user.feedbackCount)} />
        <Metric
          label="Last active"
          value={
            user.lastActiveAt ? formatRelativeTime(user.lastActiveAt, now) : "—"
          }
          sub={toDate(user.lastActiveAt)}
        />
      </section>

      {/* GROUPS LIST */}
      {showGroupsCard ? (
        <section className="rounded-2xl border border-fc-border bg-gradient-to-b from-fc-raised to-fc-overlay p-5">
          <div className="mb-4 flex items-end justify-between">
            <div>
              <div className="text-[13px] font-semibold text-fc-text">
                Groups
              </div>
              <div className="text-[11.5px] text-fc-text-dim">
                Groups list · {groups.length} total
              </div>
            </div>
          </div>
          <div className="flex flex-col">
            {groups.map(g => (
              <div
                key={g.groupId}
                className="grid grid-cols-[32px_1fr_auto_auto] items-center gap-3 border-b border-fc-border py-2.5 last:border-b-0"
              >
                <div className="grid h-8 w-8 place-items-center rounded-lg bg-fc-overlay text-[15px]">
                  {g.emoji ?? (g.isPersonal ? "👤" : "👥")}
                </div>
                <div className="min-w-0">
                  <div className="truncate text-[12.5px] font-medium text-fc-text">
                    {g.name}
                  </div>
                  <div className="truncate text-[11px] text-fc-text-dim">
                    {g.isPersonal ? "personal" : "family"} · {g.memberCount}{" "}
                    member{g.memberCount === 1 ? "" : "s"} · {g.expenseCount}{" "}
                    expenses
                  </div>
                </div>
                <Chip tone={g.role === "admin" ? "good" : "neutral"}>
                  {g.role}
                </Chip>
                <span className="font-mono text-[11px] text-fc-text-dim">
                  joined {formatRelativeTime(g.joinedAt, now)}
                </span>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {/* RECENT EXPENSES */}
      {showExpensesCard ? (
        <section className="rounded-2xl border border-fc-border bg-gradient-to-b from-fc-raised to-fc-overlay p-5">
          <div className="mb-4 flex items-end justify-between">
            <div>
              <div className="text-[13px] font-semibold text-fc-text">
                Recent expenses
              </div>
              <div className="text-[11.5px] text-fc-text-dim">
                As payer · last {recentExpenses.length}
              </div>
            </div>
          </div>
          <div className="flex flex-col">
            {recentExpenses.map(e => (
              <div
                key={e.id}
                className="grid grid-cols-[32px_1fr_auto] items-center gap-3 border-b border-fc-border py-2.5 last:border-b-0"
              >
                <CategoryIcon emoji={e.categoryEmoji} size="sm" />
                <div className="min-w-0">
                  <div className="truncate text-[12.5px] text-fc-text">
                    <span className="font-semibold">{e.categoryName}</span>
                    <span className="text-fc-text-dim"> · </span>
                    <span>{e.groupName}</span>
                    {e.note ? (
                      <>
                        <span className="text-fc-text-dim"> · </span>
                        <span className="text-fc-text-secondary">{e.note}</span>
                      </>
                    ) : null}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-0.5">
                  <span className="font-mono text-[12.5px] font-semibold text-fc-text">
                    {formatInr(e.amount)}
                  </span>
                  <span className="font-mono text-[11px] text-fc-text-dim">
                    {formatRelativeTime(e.createdAt, now)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {/* SESSIONS */}
      {showSessionsCard ? (
        <section className="rounded-2xl border border-fc-border bg-gradient-to-b from-fc-raised to-fc-overlay p-5">
          <div className="mb-4 flex items-end justify-between">
            <div>
              <div className="text-[13px] font-semibold text-fc-text">
                Sessions
              </div>
              <div className="text-[11.5px] text-fc-text-dim">
                Refresh-token grants · {sessions.length} total
              </div>
            </div>
          </div>
          <div className="flex flex-col">
            {sessions.map(s => {
              const expiresDate =
                s.expiresAt instanceof Date ? s.expiresAt : new Date(s.expiresAt);
              const expired = expiresDate.getTime() < now.getTime();
              return (
                <div
                  key={s.id}
                  className="grid grid-cols-[1fr_auto_auto] items-center gap-3 border-b border-fc-border py-2.5 last:border-b-0"
                >
                  <div className="font-mono text-[11.5px] text-fc-text-secondary">
                    s_{s.id.slice(0, 8)}
                  </div>
                  <div className="text-[11px] text-fc-text-dim">
                    created{" "}
                    <span className="font-mono">
                      {formatRelativeTime(s.createdAt, now)}
                    </span>{" "}
                    · expires{" "}
                    <span className="font-mono">
                      {formatRelativeTime(s.expiresAt, now)}
                    </span>
                  </div>
                  <Chip tone={expired ? "warn" : "good"}>
                    {expired ? "expired" : "active"}
                  </Chip>
                </div>
              );
            })}
          </div>
        </section>
      ) : null}

      {/* PROP GRID */}
      <section className="rounded-2xl border border-fc-border bg-gradient-to-b from-fc-raised to-fc-overlay p-5">
        <div className="mb-4">
          <div className="text-[13px] font-semibold text-fc-text">Profile</div>
          <div className="text-[11.5px] text-fc-text-dim">
            Fields from schema
          </div>
        </div>
        <div className="grid grid-cols-[140px_1fr] gap-x-4 gap-y-2.5">
          <PropRow label="User ID">
            <span className="rounded-md border border-fc-border bg-fc-overlay px-2 py-0.5 font-mono text-[11.5px]">
              {user.id}
            </span>
          </PropRow>
          <PropRow label="Email">{user.email}</PropRow>
          <PropRow label="Auth provider">{user.authProvider}</PropRow>
          <PropRow label="Email verified">
            {user.emailVerified ? "yes" : "no"}
          </PropRow>
          <PropRow label="Locale · dark">
            {user.locale} · {user.darkMode ? "on" : "off"}
          </PropRow>
          <PropRow label="Active group">{user.activeGroupName ?? "—"}</PropRow>
          <PropRow label="Created">
            <span className="font-mono text-[12px]">
              {toDateTime(user.createdAt)}
            </span>
          </PropRow>
          <PropRow label="Updated">
            <span className="font-mono text-[12px]">
              {toDateTime(user.updatedAt)}
            </span>
          </PropRow>
          <PropRow label="Last active">
            <span className="font-mono text-[12px]">
              {toDateTime(user.lastActiveAt)}
            </span>
          </PropRow>
          <PropRow label="FCM token">
            {user.fcmTokenPresent ? (
              <Chip tone="good">present</Chip>
            ) : (
              <Chip>absent</Chip>
            )}
          </PropRow>
        </div>
      </section>
    </div>
  );
}
